import React, { useState } from 'react';
import apiClient from '../lib/apiClient';
import useRecaptcha from '../hooks/useRecaptcha';

const COMMON_PASSWORDS = new Set([
    'password',
    'password123',
    'qwerty',
    'qwerty123',
    '123456',
    '12345678',
    '123456789',
    '1234567890',
    'abc123',
    'admin',
    'admin123',
    'welcome',
    'welcome123',
    'letmein',
    'iloveyou',
    'monkey',
    'dragon',
    'passw0rd',
]);

const normalizePasswordForComparison = (password = '') =>
    password.normalize('NFKC').replace(/\s+/g, '').toLocaleLowerCase();

const getPasswordChecks = (password = '', confirmPassword = '') => ({
    hasMinLength: Array.from(password).length >= 12,
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[1-9]/.test(password),
    hasSpecialChar: /[^A-Za-z0-9]/.test(password),
    isNotCommonPassword: !COMMON_PASSWORDS.has(normalizePasswordForComparison(password)),
    hasPrintableCharactersOnly: /^[\x21-\x7E]+$/.test(password),
    matchesConfirmation: confirmPassword.length > 0 && password === confirmPassword,
});

const PasswordRequirement = ({ met, label }) => {
    const color = met === true ? '#1E7A3E' : met === false ? '#D81F26' : '#6E6E6E';
    const icon = met === true ? '✓' : met === false ? '✕' : '○';
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color, fontSize: '13px' }}>
            <span>{icon}</span>
            <span>{label}</span>
        </div>
    );
};

const Signup = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        termsAccepted: false,
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const passwordChecks = getPasswordChecks(formData.password, formData.confirmPassword);
    const passwordMeetsPolicy =
        passwordChecks.hasMinLength &&
        passwordChecks.hasLowercase &&
        passwordChecks.hasNumber &&
        passwordChecks.hasSpecialChar &&
        passwordChecks.isNotCommonPassword &&
        passwordChecks.hasPrintableCharactersOnly;
    const { containerRef: recaptchaRef, execute: runRecaptcha, ready: recaptchaReady, error: recaptchaError } = useRecaptcha(
        'signup',
        { size: 'normal' }
    );

    const handleChange = (e) => {
        const { name, type, value, checked } = e.target;
        setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
    };

    const handleGoogleSignup = () => {
        window.location.href = '/auth/google';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            if (!passwordMeetsPolicy) {
                throw new Error('Password does not meet the security requirements.');
            }

            if (!passwordChecks.matchesConfirmation) {
                throw new Error('Retyped password must match.');
            }

            if (!formData.termsAccepted) {
                throw new Error('You must accept the Terms of Service and Privacy Policy to create an account.');
            }

            if (recaptchaError) {
                throw new Error(recaptchaError.message || 'reCAPTCHA failed to load. Please refresh and retry.');
            }

            if (!recaptchaReady) {
                throw new Error('Security check is still initializing. Please wait a moment and try again.');
            }

            const token = await runRecaptcha();

            // Submit basic signup data
            const response = await apiClient.post('/auth/signup', {
                name: formData.name,
                email: formData.email,
                password: formData.password,
                confirmPassword: formData.confirmPassword,
                termsAccepted: formData.termsAccepted,
                token,
                'g-recaptcha-response': token,
            });

            if (response.data?.pendingVerification) {
                const params = new URLSearchParams({
                    verifyEmail: '1',
                    email: formData.email,
                });
                window.location.href = `/login?${params.toString()}`;
                return;
            }

            window.location.href = '/dashboard';
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Signup failed');
        } finally {
            setLoading(false);
        }
    };

    const styles = {
        container: {
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #3457D5 0%, #7CD845 100%)',
            padding: '20px',
        },
        card: {
            background: '#FFFFFF',
            borderRadius: '24px',
            padding: '48px',
            maxWidth: '500px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        },
        logo: {
            display: 'block',
            width: '64px',
            height: '64px',
            objectFit: 'contain',
            margin: '0 auto 12px',
        },
        logoText: {
            fontSize: '32px',
            fontWeight: '800',
            color: '#3457D5',
            marginBottom: '12px',
            textAlign: 'center',
        },
        title: {
            fontSize: '28px',
            fontWeight: '700',
            color: '#2B2B2B',
            marginBottom: '8px',
            textAlign: 'center',
        },
        subtitle: {
            fontSize: '15px',
            color: '#6E6E6E',
            marginBottom: '32px',
            textAlign: 'center',
        },
        input: {
            width: '100%',
            padding: '12px 16px',
            fontSize: '15px',
            border: '1px solid #E5E5E5',
            borderRadius: '12px',
            marginBottom: '16px',
            outline: 'none',
        },
        button: {
            width: '100%',
            padding: '16px',
            background: '#3457D5',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s',
            marginTop: '8px',
        },
        termsRow: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
            margin: '4px 0 18px',
            color: '#4A4A4A',
            fontSize: '14px',
            lineHeight: '1.5',
        },
        checkbox: {
            marginTop: '3px',
            width: '16px',
            height: '16px',
            accentColor: '#3457D5',
            flexShrink: 0,
        },
        policyLink: {
            color: '#3457D5',
            textDecoration: 'none',
            fontWeight: '600',
        },
        googleButton: {
            width: '100%',
            padding: '16px',
            background: '#FFFFFF',
            border: '2px solid #E5E5E5',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: '600',
            color: '#414141',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            transition: 'all 0.3s',
        },
        recaptchaWrapper: {
            marginTop: '8px',
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'center',
        },
        recaptchaNote: {
            fontSize: '11px',
            color: '#6E6E6E',
            textAlign: 'center',
            marginTop: '-6px',
            marginBottom: '16px',
        },
        divider: {
            display: 'flex',
            alignItems: 'center',
            textAlign: 'center',
            margin: '24px 0',
            color: '#6E6E6E',
            fontSize: '14px',
        },
        dividerLine: {
            flex: 1,
            borderBottom: '1px solid #E5E5E5',
        },
        error: {
            background: '#FFE0E0',
            color: '#D81F26',
            padding: '12px 16px',
            borderRadius: '12px',
            marginBottom: '20px',
            fontSize: '14px',
            textAlign: 'center',
        },
        link: {
            color: '#3457D5',
            textDecoration: 'none',
            fontWeight: '600',
        },
        passwordPanel: {
            background: '#F8FAFC',
            border: '1px solid #E5E7EB',
            borderRadius: '12px',
            padding: '14px 16px',
            marginBottom: '16px',
        },
        passwordPanelTitle: {
            fontSize: '12px',
            fontWeight: '700',
            color: '#475569',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            marginBottom: '10px',
        },
        footer: {
            marginTop: '24px',
            textAlign: 'center',
            fontSize: '14px',
            color: '#6E6E6E',
        },
    };

    return (
        <div style={styles.container}>
            <div style={styles.card}>
                <img src="/corpusio-logo.webp" alt="Corpusio" style={styles.logo} />
                <div style={styles.logoText}>Corpusio</div>
                <h1 style={styles.title}>Create Account</h1>
                <p style={styles.subtitle}>Start your AI journey with Corpusio</p>

                {error && <div style={styles.error}>{error}</div>}
                {recaptchaError && !error && (
                    <div style={{ ...styles.error, marginBottom: '20px' }}>
                        {recaptchaError.message || 'Unable to load reCAPTCHA. Refresh and try again.'}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <input
                        type="text"
                        name="name"
                        placeholder="Full Name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        autoComplete="name"
                        style={styles.input}
                    />
                    <input
                        type="email"
                        name="email"
                        placeholder="Email Address"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        autoComplete="email"
                        style={styles.input}
                    />
                    <input
                        type="password"
                        name="password"
                        placeholder="Create a strong password"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        autoComplete="new-password"
                        style={styles.input}
                    />
                    <input
                        type="password"
                        name="confirmPassword"
                        placeholder="Confirm Password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        required
                        autoComplete="new-password"
                        style={styles.input}
                    />
                    <div style={styles.passwordPanel}>
                        <div style={styles.passwordPanelTitle}>Password requirements</div>
                        <div style={{ display: 'grid', gap: '8px' }}>
                            <PasswordRequirement met={formData.password ? passwordChecks.hasMinLength : null} label="At least 12 characters" />
                            <PasswordRequirement met={formData.password ? passwordChecks.hasLowercase : null} label="Include at least one lowercase letter (a-z)" />
                            <PasswordRequirement met={formData.password ? passwordChecks.hasNumber : null} label="Include at least one number (1-9)" />
                            <PasswordRequirement met={formData.password ? passwordChecks.hasSpecialChar : null} label="Include at least one special character" />
                            <PasswordRequirement met={formData.password ? passwordChecks.isNotCommonPassword : null} label="Avoid common passwords" />
                            <PasswordRequirement met={formData.password || formData.confirmPassword ? passwordChecks.matchesConfirmation : null} label="Retyped password must match" />
                            <PasswordRequirement met={formData.password ? passwordChecks.hasPrintableCharactersOnly : null} label="Use only ASCII characters with no spaces" />
                        </div>
                    </div>
                    <label style={styles.termsRow}>
                        <input
                            type="checkbox"
                            name="termsAccepted"
                            checked={formData.termsAccepted}
                            onChange={handleChange}
                            required
                            style={styles.checkbox}
                        />
                        <span>
                            I agree to the <a href="/terms-of-service" style={styles.policyLink}>Terms of Service</a> and{' '}
                            <a href="/privacy-policy" style={styles.policyLink}>Privacy Policy</a>.
                        </span>
                    </label>

                    <button type="submit" style={styles.button} disabled={loading}>
                        {loading ? 'Creating Account...' : 'Sign Up'}
                    </button>
                </form>
                <div style={styles.recaptchaWrapper}>
                    <div ref={recaptchaRef} />
                </div>
                <p style={styles.recaptchaNote}>
                    Protected by reCAPTCHA and the Google <a href="https://policies.google.com/privacy">Privacy Policy</a> and{' '}
                    <a href="https://policies.google.com/terms">Terms of Service</a> apply.
                </p>

                <div style={styles.divider}>
                    <div style={styles.dividerLine}></div>
                    <span style={{ padding: '0 10px' }}>OR</span>
                    <div style={styles.dividerLine}></div>
                </div>

                <button style={styles.googleButton} onClick={handleGoogleSignup}>
                    <svg width="24" height="24" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    Sign up with Google
                </button>

                <div style={styles.footer}>
                    Already have an account? <a href="/login" style={styles.link}>Sign in</a>
                </div>
            </div>
        </div>
    );
};

export default Signup;
